import { submitAccessRequest, checkExistingRequest } from '../lib/accessRequests';
import { AccessRequest } from '../types';
import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, AlertTriangle } from 'lucide-react';
import { AppUser, useAuthMeta } from './AuthProvider';

interface LoginProps {
    user?: AppUser | null;
}

export function Login({ user }: LoginProps) {
    const { logout } = useAuthMeta();
    const [accessDenied, setAccessDenied] = useState(!!user);
    const [deniedEmail, setDeniedEmail] = useState(user?.email || '');
    const [requestStatus, setRequestStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [existingRequest, setExistingRequest] = useState<AccessRequest | null>(null);
    const [checkFailed, setCheckFailed] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        if (user?.email) {
            setAccessDenied(true);
            setDeniedEmail(user.email);
            checkStatus(user.email);
        }
    }, [user]);

    const checkStatus = async (email: string) => {
        try {
            setCheckFailed(false);
            const existingReq = await checkExistingRequest(email);
            setExistingRequest(existingReq);
        } catch (err) {
            console.error('Error checking existing request:', err);
            setCheckFailed(true);
        }
    };

    const handleGoogleLogin = () => {
        // Server-side OAuth: redirect to the backend, which bounces to Google and
        // returns to "/" with a session cookie set.
        setIsRedirecting(true);
        window.location.href = '/api/auth/google/login';
    };

    const handleRequestAccess = async () => {
        if (!deniedEmail) return;

        setRequestStatus('submitting');
        setErrorMessage('');

        try {
            await submitAccessRequest(user || { email: deniedEmail });
            setRequestStatus('success');
            await checkStatus(deniedEmail);
        } catch (error) {
            console.error('Error requesting access:', error);
            setRequestStatus('error');
            setErrorMessage(error instanceof Error ? error.message : 'Failed to submit access request. Please try again.');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Clock className="w-5 h-5 text-yellow-600" />;
            case 'approved':
                return <CheckCircle className="w-5 h-5 text-green-600" />;
            case 'denied':
                return <XCircle className="w-5 h-5 text-red-600" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200';
            case 'approved':
                return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200';
            case 'denied':
                return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200';
            default:
                return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-slate-700/50 dark:border-slate-600 dark:text-slate-200';
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
            <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="text-center">
                    <h2 className="text-3xl font-extrabold text-slate-900 dark:text-slate-100">
                        Welcome to TRB's Subtitle Studio
                    </h2>
                    <p className="mt-2 text-slate-600 dark:text-slate-400">
                        {existingRequest?.status === 'approved'
                            ? 'Access Request Approved'
                            : accessDenied
                                ? 'Access Restricted'
                                : 'Please sign in to continue'}
                    </p>
                </div>

                {!accessDenied ? (
                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={isRedirecting}
                        className="w-full flex items-center justify-center gap-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg px-4 py-3 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-70 disabled:cursor-wait"
                    >
                        {isRedirecting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-500 dark:border-slate-300"></div>
                                Redirecting to Google…
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                </svg>
                                Sign in with Google
                            </>
                        )}
                    </button>
                ) : (
                    <div className="space-y-4">
                        {(!existingRequest || existingRequest.status !== 'approved') && (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                <div className="flex items-start">
                                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                            Access Denied
                                        </h3>
                                        <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                            Your email <span className="font-semibold">{deniedEmail}</span> is not authorized to access this application.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {checkFailed && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                                <div className="flex items-start">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                            Unable to check request status
                                        </h3>
                                        <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                                            We couldn't check if you have a pending request. Please try again in a moment.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {existingRequest ? (
                            <div className={`border rounded-lg p-4 ${getStatusColor(existingRequest.status)}`}>
                                <div className="flex items-start">
                                    {getStatusIcon(existingRequest.status)}
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium">
                                            Access Request {existingRequest.status.charAt(0).toUpperCase() + existingRequest.status.slice(1)}
                                        </h3>
                                        <p className="mt-1 text-sm">
                                            {existingRequest.status === 'pending' && 'Your access request has been received — TRB will review it soon.'}
                                            {existingRequest.status === 'approved' && 'Your access has been approved! Please refresh the page to continue.'}
                                            {existingRequest.status === 'denied' && (
                                                existingRequest.notes
                                                    ? `Reason: ${existingRequest.notes}`
                                                    : 'Your access request was denied.'
                                            )}
                                        </p>
                                        <p className="mt-1 text-xs opacity-75">
                                            Requested: {existingRequest.requestedAt.toLocaleDateString()}
                                        </p>
                                        {existingRequest.status === 'approved' && (
                                            <button
                                                type="button"
                                                onClick={() => window.location.reload()}
                                                className="mt-3 w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
                                            >
                                                Refresh Page
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : requestStatus === 'success' ? (
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                <div className="flex items-start">
                                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                                            Request Submitted
                                        </h3>
                                        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                                            Your access request has been received — TRB will review it soon.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handleRequestAccess}
                                    disabled={requestStatus === 'submitting'}
                                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg px-4 py-3 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:cursor-not-allowed"
                                >
                                    {requestStatus === 'submitting' ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Request Access'
                                    )}
                                </button>

                                {requestStatus === 'error' && errorMessage && (
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                        <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                                    </div>
                                )}
                            </>
                        )}

                        <button
                            type="button"
                            onClick={() => { logout(); }}
                            className="w-full text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
                        >
                            ← Back to Sign In
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
